import axios, { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from 'src/hooks/useAuth';
import {
  addUserProperty,
  getEducationLevelGroupSlug,
} from 'src/modules/analytics/analytics';
import dayjs from 'dayjs';
import { User, UserProfile, RefereeCountResponse } from 'src/types/user.types';
import {
  clearOnboardingData,
  getAllOnboardingData,
} from 'src/utils/onboardingStorage';
import { useUser } from 'src/contexts/UserContext';
import { isValidUserId } from 'src/utils/userId';
import { queryKeys } from 'src/queries/keys';

export const useCurrentUser = (params?: { language: string }) => {
  const { isLoggedIn, logout } = useAuth();
  return useQuery<User>({
    queryFn: async () => {
      const { data } = await axios.get('/api/user', {
        params,
      });
      return data;
    },
    queryKey: queryKeys.user(),
    enabled: isLoggedIn,
    onError: async (error: unknown) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          await logout();
        }
      }
    },
  });
};

export const useGetUserProfile = (userId: string) => {
  return useQuery<UserProfile>({
    queryFn: async () => {
      const { data } = await axios.get(`/api/users/${userId}/profile`);
      return data;
    },
    queryKey: queryKeys.userProfile(userId),
    enabled: isValidUserId(userId),
  });
};

export const useUpdateCurrentUser = () => {
  const queryClient = useQueryClient();
  const { me } = useUser();

  return useMutation({
    mutationFn: async (
      user:
        | Partial<
            User & {
              short_trial?: boolean;
              free_first_lesson?: boolean;
            }
          >
        | User,
    ): Promise<User> => {
      const onboardingData = getAllOnboardingData(me.id);

      const mergedData = {
        ...onboardingData,
        ...user,
      };

      const { data } = await axios.put('/api/user', mergedData);
      return data;
    },
    onSuccess: (data, updatedProperties) => {
      clearOnboardingData(data.id);
      queryClient.setQueryData(queryKeys.user(), data);
      queryClient.invalidateQueries(queryKeys.users(), { exact: true });

      if (updatedProperties.date_of_birth) {
        addUserProperty('yob', dayjs(updatedProperties.date_of_birth).year());
      }
      if (updatedProperties.school_level) {
        addUserProperty('grade_level', updatedProperties.school_level);
      }
      if (updatedProperties.education_level_code) {
        addUserProperty(
          'education_level_code',
          updatedProperties.education_level_code,
        );
        const groupSlug = getEducationLevelGroupSlug(
          updatedProperties.education_level_code,
          data.country,
        );
        if (groupSlug) {
          addUserProperty('education_level_group', groupSlug);
          addUserProperty('school_group', groupSlug);
        }
      }
      if (updatedProperties.school_id) {
        addUserProperty('school_id', updatedProperties.school_id);
      }
    },
  });
};

export type PokeResponse = {
  id: number;
  sender_id: number;
  receiver_id: number;
  message?: string;
  created_at: string;
  updated_at: string;
};

export type PokeError = { message: string } & (
  | {
      error_code: 'poke_self' | 'poke_already' | 'poke_not_friend';
    }
  | {
      error_code: 'poke_cooldown';
      error_params?: {
        max_cooldown_time_seconds?: number;
      };
    }
);

export const useCreatePoke = () => {
  return useMutation<PokeResponse, AxiosError<PokeError>, { userId: number }>({
    mutationFn: async ({ userId }) => {
      const { data: response } = await axios.post<PokeResponse>(
        `/api/users/${userId}/pokes`,
      );
      return response;
    },
  });
};

export const useCheckPseudoAvailability = (pseudo?: string) => {
  return useQuery<{ id: number; pseudo: string; uuid: string }[]>({
    queryFn: async () => {
      const { data } = await axios.get('/api/users/search', {
        params: { pseudo },
      });
      return data;
    },
    queryKey: queryKeys.usersAvailability(pseudo),
    enabled: !!pseudo && pseudo.length > 0,
    staleTime: 30 * 1000,
    retry: false,
  });
};

export const useRefereesCount = () => {
  const { isLoggedIn } = useAuth();
  return useQuery<RefereeCountResponse>({
    queryFn: async () => {
      const { data } = await axios.get('/api/user/referees/count');
      return data;
    },
    queryKey: queryKeys.refereesCount(),
    enabled: isLoggedIn,
  });
};

export const updateUserTimezone = (timezone: string) =>
  axios.put('/api/user', { timezone });
